/**
 * How often a sound is allowed to happen. Pure, so it can be tested without an audio stack.
 *
 * The scene can ask for far more sound than a person can stand. Twenty zombies hammering a wall
 * fire an `impact` event roughly every 70 ms between them; a spam-clicker generates thirty presses
 * a second; two zombies in the same square groan on the same tick often enough to sound like one
 * zombie with an echo. Played as asked, that is not a siege, it is a fuzz pedal — and the fix
 * cannot live in the synthesis, because each of those sounds is correct on its own.
 *
 * So the rate limit is the instrument. Four thuds a second is a *rhythm*: fast enough to read as a
 * crowd hammering, slow enough that every hit is a separate event to the ear. Thirty presses a
 * second therefore make a steady 4 Hz tick rather than noise, which is also the honest feedback —
 * the server is only taking twenty clicks per 200 ms batch anyway.
 *
 * Each rule is a sliding window rather than a fixed one: a fixed window lets eight thuds through
 * across a boundary (four at 0.99 s, four at 1.01 s), which is exactly the burst it exists to stop.
 */

export interface LimitRule {
  /** Plays allowed inside one window. */
  readonly max: number
  readonly windowMs: number
}

/**
 * The limits from `docs/tasks/F21-sound.md` §2. Numbers live here and nowhere else.
 *
 * `groan` is per square rather than global: three squares under siege at once are three separate
 * crowds on screen, and silencing two of them because the third just groaned would make the page
 * quieter the worse the outage gets.
 */
export const SOUND_LIMITS = {
  thud: { max: 4, windowMs: 1_000 },
  groan: { max: 1, windowMs: 1_500 },
  gate_crash: { max: 1, windowMs: 5_000 },
  reset_fanfare: { max: 1, windowMs: 5_000 },
} as const satisfies Record<string, LimitRule>

export type LimitKind = keyof typeof SOUND_LIMITS

export interface Limiter {
  /**
   * Whether this play may happen now, counting it if so.
   *
   * `scope` splits the budget — the groan rule is per square, so the caller passes the provider.
   * Omit it for a global rule.
   */
  allow(kind: LimitKind, nowMs: number, scope?: string): boolean
  reset(): void
}

export function createLimiter(limits: Record<LimitKind, LimitRule> = SOUND_LIMITS): Limiter {
  // One array of timestamps per bucket, never longer than the rule's `max`. At four kinds and
  // three squares that is a handful of numbers, so pruning by copy costs nothing and there is no
  // ring-buffer index to get wrong.
  const seen = new Map<string, number[]>()

  return {
    allow(kind, nowMs, scope) {
      const rule = limits[kind]
      const key = scope === undefined ? kind : `${kind}:${scope}`
      const previous = seen.get(key)
      const recent: number[] = []
      if (previous) {
        for (const at of previous) {
          if (nowMs - at < rule.windowMs) recent.push(at)
        }
      }
      if (recent.length >= rule.max) {
        seen.set(key, recent)
        return false
      }
      recent.push(nowMs)
      seen.set(key, recent)
      return true
    },
    reset() {
      seen.clear()
    },
  }
}
